import React from "react";
import DOMPurify from "dompurify";
import {
  FaFileAlt,
  FaImage,
  FaListOl,
  FaPen,
  FaTimes,
  FaTrophy,
} from "react-icons/fa";

import {
  getMediaUrl,
  stripHtml,
} from "../utils/disciplinesUtils";

function DetailItem({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        {Icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Icon />
          </span>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function DisciplineDetails({ discipline, onEdit, onClose }) {
  const imageUrl = getMediaUrl(discipline.image_discipline);
  const hasContent = Boolean(stripHtml(discipline.contenu_discipline));

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="relative h-72 bg-slate-100 dark:bg-slate-800">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={discipline.nom_discipline}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-slate-400">
              <FaImage className="text-5xl" />
              <p className="mt-3 text-sm font-bold">Aucune image renseignée</p>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-5">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-orange-200">
              Discipline
            </p>
            <h3 className="mt-1 text-3xl font-bold text-white">
              {discipline.nom_discipline}
            </h3>
          </div>
        </div>

        <div className="p-5">
          <p className="text-base font-semibold leading-7 text-slate-700 dark:text-slate-200">
            {discipline.description_discipline || "Description non renseignée."}
          </p>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Informations
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem
            label="Nom"
            value={discipline.nom_discipline}
            icon={FaTrophy}
          />

          <DetailItem
            label="Ordre d'affichage"
            value={discipline.ordre_discipline ?? "—"}
            icon={FaListOl}
          />

          <div className="sm:col-span-2">
            <DetailItem
              label="Image"
              value={discipline.image_discipline || "Non renseignée"}
              icon={FaImage}
            />
          </div>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Contenu détaillé
        </h4>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
          {hasContent ? (
            <div
              className="prose max-w-none prose-slate dark:prose-invert"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(discipline.contenu_discipline),
              }}
            />
          ) : (
            <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
              <FaFileAlt />
              <p className="text-sm">Aucun contenu détaillé renseigné.</p>
            </div>
          )}
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaPen />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
}